// @refresh reset
import { type CrowdexUIMessage, steerMessageIds } from '@crowdex/agent-contracts';
import type { InlineReferenceEditorHandle } from '@crowdex/ui/components/inline-reference-editor';
import { type CSSProperties, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { ConversationActionsProvider } from '@/components/conversation-pane-parts';
import {
  ConversationPanelRoot,
  ConversationPaneSlot,
  WorkspaceDockLayout,
} from '@/components/conversation-panel-layout';
import type { AcpSelectConfigOption } from '@/components/desktop-config-controls';
import { ComposerMenuRow } from '@/components/desktop-shell/composer-menu-row';
import { ConversationDialogs } from '@/components/desktop-shell/conversation-dialogs';
import { ConversationDockOverlays } from '@/components/desktop-shell/conversation-dock-overlays';
import type { ConversationHeaderProps } from '@/components/desktop-shell/conversation-header';
import { ConversationPane } from '@/components/desktop-shell/conversation-pane';
import { ConversationSidePanel } from '@/components/desktop-shell/conversation-side-panel';
import { ConversationSummary } from '@/components/desktop-shell/conversation-summary';
import { DesktopNavRail } from '@/components/desktop-shell/desktop-nav-rail';
import type { DesktopShellProps } from '@/components/desktop-shell/desktop-shell-props';
import { DesktopSidebar, HiddenSidebar } from '@/components/desktop-shell/desktop-sidebar';
import { DesktopUpdateBanner } from '@/components/desktop-shell/desktop-update-banner';
import { SplitConversationPanes } from '@/components/desktop-shell/split-conversation-panes';
import { useShellNavigation } from '@/components/desktop-shell/use-shell-navigation';
import type { MentionTarget } from '@/components/desktop-side-panel';
import { MobileShell } from '@/components/mobile-shell/mobile-shell';
import { AllAppsPage, useAllAppsOpen } from '@/components/sidebar/all-apps';
import { ShellSearchDialog } from '@/components/sidebar/search-dialog';
import { ShellNavigationProvider } from '@/components/sidebar/shell-navigation';
import type { ExecutionMode } from '@/components/worktree-controls';
import { useCloudHandoffDialog } from '@/hooks/use-cloud-handoff-dialog';
import { SIDEBAR_RAIL_WIDTH, useDesktopSidebar } from '@/hooks/use-desktop-sidebar';
import { useEnvironmentSettings } from '@/hooks/use-environment-settings';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useWorktreeSettings } from '@/hooks/use-worktree-settings';
import { resolveDesktopTheme } from '@/lib/app-settings';
import { useWorkspaceDirectory } from '@/lib/assistants/directory';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import { messagesChangedPaths } from '@/lib/conversations/message-changes';
import {
  PREVIEW_REVIEW_DIFF,
  PREVIEW_SKILLS,
  previewConfigOptions,
} from '@/lib/desktop-preview-data';
import { createAgentHandoff } from '@/lib/desktop-shell/agent-handoff';
import { createBranchControls } from '@/lib/desktop-shell/branch-controls';
import { createCloudHandoffOptions } from '@/lib/desktop-shell/cloud-handoff-options';
import { cloudProjectMissingCard } from '@/lib/desktop-shell/cloud-start-recovery';
import { buildComposerContextTargets } from '@/lib/desktop-shell/composer-context-targets';
import { createComposerSubmit } from '@/lib/desktop-shell/composer-submit';
import {
  desktopConnectionState,
  resolveCloudStartupPhase,
} from '@/lib/desktop-shell/connection-status';
import { createConversationNavigation } from '@/lib/desktop-shell/conversation-navigation';
import { useConversationOpenerValue } from '@/lib/desktop-shell/conversation-opener';
import { useConversationTitleSync } from '@/lib/desktop-shell/conversation-title-sync';
import { createConversationTurnControls } from '@/lib/desktop-shell/conversation-turn-controls';
import { DESKTOP_LAYOUT_QUERY, NARROW_DESKTOP_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { createExecutionModeControls } from '@/lib/desktop-shell/execution-mode-controls';
import { followUpUiMessage } from '@/lib/desktop-shell/follow-up-content';
import { answeredQuestion, useFollowUpDrain } from '@/lib/desktop-shell/follow-up-drain';
import { createMessageChangeControls } from '@/lib/desktop-shell/message-change-controls';
import {
  pendingCloudUiMessage as derivePendingCloudUiMessage,
  previewConversationTitle as derivePreviewConversationTitle,
  latestUsage,
} from '@/lib/desktop-shell/message-derivations';
import { createMessageEditControls } from '@/lib/desktop-shell/message-edit-controls';
import { createRepositoryActions } from '@/lib/desktop-shell/repository-actions';
import { useCloudRuntime } from '@/lib/desktop-shell/use-cloud-runtime';
import { useComposerDraft } from '@/lib/desktop-shell/use-composer-draft';
import { useConversationArtifacts } from '@/lib/desktop-shell/use-conversation-artifacts';
import { useConversationController } from '@/lib/desktop-shell/use-conversation-controller';
import { useConversationFailures } from '@/lib/desktop-shell/use-conversation-failures';
import { useConversationPersistence } from '@/lib/desktop-shell/use-conversation-persistence';
import { useConversationReferenceResolver } from '@/lib/desktop-shell/use-conversation-reference-resolver';
import { useConversationSwitchEffects } from '@/lib/desktop-shell/use-conversation-switch-effects';
import { useDesktopAgentConnection } from '@/lib/desktop-shell/use-desktop-agent-connection';
import { useDesktopConversationChat } from '@/lib/desktop-shell/use-desktop-conversation-chat';
import { useDesktopRuntimeBootstrap } from '@/lib/desktop-shell/use-desktop-runtime-bootstrap';
import { useDesktopShellView } from '@/lib/desktop-shell/use-desktop-shell-view';
import { useDesktopTheme } from '@/lib/desktop-shell/use-desktop-theme';
import { useFollowUpQueue } from '@/lib/desktop-shell/use-follow-up-queue';
import { useGitStatusSync } from '@/lib/desktop-shell/use-git-status-sync';
import { useInitialDesktopShellStore } from '@/lib/desktop-shell/use-initial-desktop-shell-store';
import { useInitialThreadHydration } from '@/lib/desktop-shell/use-initial-thread-hydration';
import { useLeaveUnlinkedCloud } from '@/lib/desktop-shell/use-leave-unlinked-cloud';
import { usePendingMessageDrain } from '@/lib/desktop-shell/use-pending-message-drain';
import { usePullRequestSync } from '@/lib/desktop-shell/use-pull-request-sync';
import { useSidePanelShortcuts } from '@/lib/desktop-shell/use-side-panel-shortcuts';
import { useSidebarModel } from '@/lib/desktop-shell/use-sidebar-model';
import { useSidebarRail } from '@/lib/desktop-shell/use-sidebar-rail';
import { useSplitPaneFocus } from '@/lib/desktop-shell/use-split-pane-focus';
import { useTitleBarInsets } from '@/lib/desktop-shell/use-title-bar-insets';
import {
  setFolderEnvironment,
  useWorkspaceEnvironmentSync,
} from '@/lib/desktop-shell/use-workspace-environment-sync';
import { useWorkspaceSyncBootstrap } from '@/lib/desktop-shell/use-workspace-sync-bootstrap';
import { createWorkspaceActions } from '@/lib/desktop-shell/workspace-actions';
import { createWorktreeLifecycle } from '@/lib/desktop-shell/worktree-lifecycle';
import { isNativeAgentReadyForWorkspace } from '@/lib/native-agent/native-agent-readiness';
import { killNativeAgentBackgroundTask, killNativeAgentTerminal } from '@/lib/native-desktop';
import { useLibraryPage } from '@/lib/navigation';
import { activePanelView, showPanelView } from '@/lib/side-panel/panel-tabs';
import { sourceControlRepositoryFullName } from '@/lib/sidebar-threads';
import { ConversationProviders } from './desktop-shell/conversation-providers';
import { ShellServices } from './desktop-shell/shell-services';
export function DesktopShell({
  pluginPermissions = new Set<string>(),
  email,
  initialChatId,
  initialPanelView,
  initialWorkspace,
  usePersonalWorkspace = false,
  restoreInitialThread = false,
  preview = false,
  previewConversation = false,
  previewSetup = null,
}: DesktopShellProps) {
  const allAppsOpen = useAllAppsOpen();
  const desktopStore = useInitialDesktopShellStore(
    initialChatId,
    initialPanelView,
    initialWorkspace,
    !preview,
  );
  const [initialThreadHydrationStatus, setInitialThreadHydrationStatus] = useState<
    'waiting' | 'loading' | 'ready' | 'error'
  >(restoreInitialThread && !preview ? 'waiting' : 'ready');
  const {
    appSettings,
    setAddRepositoryOpen,
    native,
    agents,
    agentId,
    workspace,
    discoveringAgents,
    cloudRuntime,
    cloudProvisioningPhase,
    cloudProvisionError,
    cloudProjectMissing,
    pendingCloudMessage,
    cloudHandoffContext,
    cloudRepositoryBusy,
    conversationPlacementPending,
    localHandoffAgentId,
    localHandoffWorking,
    chatId,
    initialTranscript,
    restoredCollaboration,
    restoredModelId,
    messageRevision,
    conversationCache,
    syncedThreads,
    setSyncedThreads,
    setSyncedAgentConfigs,
    setSyncedPullRequests,
    syncedWorkspaces,
    setSyncedWorkspaces,
    setWorkspaceSyncError,
    setLoadingThreadIds,
    generatedConversationTitle,
    setGeneratedConversationTitle,
    conversationLayout,
    previewModel,
    setPreviewModel,
    previewEffort,
    setPreviewEffort,
    sidePanelOpen,
    setSidePanelOpen,
    sidePanelView,
    setPanelTabs,
    selectedSubagentId,
    setSelectedSubagentId,
    queuedFollowUps,
    steeredFollowUps,
    pausedFollowUpQueues,
    editingMessageId,
    editingMessageContainer,
    gitStatus,
    setLocalBranches,
    cloudPullRequest,
    executionMode,
    worktreeSourceRoot,
    workspaceEnvironment,
    panelMentionTargets,
    personalWorkspaceRoot,
  } = useDesktopShellView(desktopStore);
  const [composerText, setComposerText] = useComposerDraft({
    store: desktopStore,
    chatId,
  });
  useDesktopTheme({ store: desktopStore, appSettings });
  const cloudRuntimeRef = useRef<CloudRuntime | null>(null);
  cloudRuntimeRef.current = cloudRuntime;
  const cloudDisconnectedRef = useRef(false);
  const {
    cloudHandoffRepository,
    setCloudHandoffRepository,
    cloudHandoffBranches,
    setCloudHandoffBranches,
    cloudHandoffSource,
    setCloudHandoffSource,
    cloudHandoffAgentId,
    setCloudHandoffAgentId,
    cloudHandoffWorking,
    setCloudHandoffWorking,
    cloudHandoffError,
    setCloudHandoffError,
    cloudHandoffRemoteNotice,
    setCloudHandoffRemoteNotice,
  } = useCloudHandoffDialog();
  const {
    mode: chosenSidebarMode,
    removedProjects,
    revealed: sidebarRevealed,
    reconcileRemovedProjects,
    restoreProject,
    reveal: revealSidebar,
    scheduleClose: scheduleSidebarClose,
    setMode: setSidebarMode,
    toggleHidden: toggleSidebarHidden,
  } = useDesktopSidebar();
  const workspaceSplit = conversationLayout.kind === 'split';
  const suppressThreadClickRef = useRef(false);
  const workspaceContainerRef = useRef<HTMLElement>(null);
  // A phone gets an app of its own (`MobileShell`) around the same conversation.
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  const oppositeFollowUpModeRef = useRef(false);
  const composerRef = useRef<InlineReferenceEditorHandle>(null);
  const focusComposerAfterSwitchRef = useRef(false);
  const pendingExecutionAfterLocalRef = useRef<ExecutionMode | null>(null);
  const provisionCloudWorkspaceRef = useRef<() => Promise<boolean>>(async () => true);
  const environment = useEnvironmentSettings({
    workspace,
    worktreeSourceRoot,
    workspaceEnvironment,
    setWorkspaceEnvironment: (root, written) => setFolderEnvironment(desktopStore, root, written),
    preview,
  });
  const worktreeSettings = useWorktreeSettings(preview);
  useWorkspaceEnvironmentSync({
    store: desktopStore,
    preview,
    workspace,
    worktreeSourceRoot,
  });
  useConversationSwitchEffects({
    store: desktopStore,
    preview,
    chatId,
    focusAfterSwitchRef: focusComposerAfterSwitchRef,
    composerRef,
  });
  const activeChatIdRef = useRef(chatId);
  const initialChatIdRef = useRef(chatId);
  const selectionRequestRef = useRef(0);
  const initialHydrationRequestRef = useRef(0);
  const initialHydrationAttemptsRef = useRef(0);
  const initialHydrationRetryTimerRef = useRef<number | null>(null);
  activeChatIdRef.current = chatId;
  const referenceResolverRef = useRef<
    (kind: MentionTarget['kind'], id: string) => Promise<unknown>
  >(async () => undefined);
  const { refreshAgents } = useDesktopRuntimeBootstrap({
    store: desktopStore,
    appSettings,
    initialWorkspace,
    usePersonalWorkspace,
    preview,
    previewSetup,
  });
  useTitleBarInsets({ store: desktopStore, native, preview });
  useGitStatusSync({
    store: desktopStore,
    preview,
    workspace,
    chatId,
    executionMode,
  });
  useEffect(() => {
    if (gitStatus === null) return;
    setLocalBranches(gitStatus.branches);
  }, [gitStatus, setLocalBranches]);
  usePullRequestSync({
    store: desktopStore,
    preview,
    workspace,
    chatId,
    gitStatus,
    syncedWorkspaces,
    executionMode,
  });
  const {
    agent,
    agentRuntime,
    agentWorkspace,
    cloudAgentSelected,
    connection,
    conversationModelId,
    selectedCloudRuntime,
  } = useDesktopAgentConnection({
    preview,
    hydrationReady: initialThreadHydrationStatus === 'ready',
    chatId,
    agents,
    agentId,
    restoredModelId,
    restoredCollaboration,
    executionMode,
    workspace,
    syncedWorkspaces,
    workspaceEnvironment,
    cloudRuntime,
    cloudHandoffContext,
    generatedConversationTitle,
    referenceResolverRef,
    personalWorkspaceRoot,
  });
  useSidePanelShortcuts(desktopStore, connection.pluginWorkbench.panels);
  const {
    answerToolApproval,
    messages,
    sendMessage,
    status,
    setMessages,
    error,
    chatStoreRef,
    conversations,
    respondToInteraction,
    sendInBackground,
    stopAgentResponse,
  } = useDesktopConversationChat({
    store: desktopStore,
    connection,
    chatId,
    initialTranscript,
    cloudAgentSelected,
    selectedCloudRuntime,
    initialThreadHydrationReady: initialThreadHydrationStatus === 'ready',
    agentRuntime,
    agentId,
    agents,
  });
  const pendingCloudUiMessage = useMemo<CrowdexUIMessage | null>(
    () => derivePendingCloudUiMessage(chatId, pendingCloudMessage),
    [chatId, pendingCloudMessage],
  );
  const baseDisplayedMessages =
    pendingCloudUiMessage === null ? messages : [...messages, pendingCloudUiMessage];
  const activeSteeredFollowUps = steeredFollowUps[chatId] ?? [];
  // A steer the agent took is part of the turn's message, where it was sent; until then it
  // waits below the conversation.
  const placedSteerIds = steerMessageIds(baseDisplayedMessages);
  const displayedMessageIds = new Set(baseDisplayedMessages.map((message) => message.id));
  const displayedMessages = [
    ...baseDisplayedMessages,
    ...activeSteeredFollowUps
      .filter(
        (followUp) => !displayedMessageIds.has(followUp.id) && !placedSteerIds.has(followUp.id),
      )
      .map(followUpUiMessage),
  ];
  const messageChanges = createMessageChangeControls({
    conversation: conversations.of(chatId),
  });
  const { handoffRemoteProvider, submitMessage } = useCloudRuntime({
    store: desktopStore,
    conversations,
    preview,
    agentCommand: agent?.command,
    chatId,
    cloudAgentSelected,
    cloudRuntime,
    sidePanelOpen,
    sidePanelView,
    sendMessage,
    cloudRuntimeRef,
    cloudDisconnectedRef,
    provisionCloudWorkspaceRef,
  });
  const {
    busy,
    conversationRefs,
    conversationSources,
    displayedPlan,
    markAgentTerminalStopped,
    selectedSubagent,
    visibleAgentTerminals,
  } = useConversationArtifacts({
    messages,
    setMessages,
    status,
    previewConversation,
    selectedSubagentId,
  });
  // Commits and Review scope to the files this conversation's turns changed.
  const conversationFiles = useMemo(() => messagesChangedPaths(messages), [messages]);
  useEffect(() => {
    const { conversationFiles: current, setConversationFiles } = desktopStore.getState();
    if (
      current.own.join('\0') !== conversationFiles.own.join('\0') ||
      current.shared.join('\0') !== conversationFiles.shared.join('\0')
    )
      setConversationFiles(conversationFiles);
  }, [conversationFiles, desktopStore]);
  function openSubagent(toolCallId: string): void {
    setSelectedSubagentId(toolCallId);
    setPanelTabs((tabs) => showPanelView(tabs, 'subagent'));
    setSidePanelOpen(true);
  }
  const agentReady =
    preview ||
    isNativeAgentReadyForWorkspace(
      connection.conversationId,
      connection.conversationWorkspaceRoot,
      agentWorkspace,
    );
  const ready = initialThreadHydrationStatus === 'ready' && agentReady && !busy;
  const activeQueuedFollowUps = queuedFollowUps[chatId] ?? [];
  const followUpQueuePaused = pausedFollowUpQueues[chatId] !== undefined;
  const { removeQueuedFollowUp, enqueueFollowUp, reorderQueuedFollowUp, clearQueuedFollowUps } =
    useFollowUpQueue(desktopStore);
  const { sendQueuedFollowUp, steerFollowUp } = useFollowUpDrain({
    store: desktopStore,
    chatId,
    busy,
    // A cloud conversation's first message starts its sandbox: it needs no agent connected yet.
    agentReady: agentReady || (cloudAgentSelected && selectedCloudRuntime === null),
    activeSteeredFollowUps,
    activeQueuedFollowUps,
    followUpQueuePaused,
    steer: async (message, messageId) =>
      message.files.length === 0 &&
      (await connection.steerGeneration(message.text, messageId, answeredQuestion(message))),
    // Through the cloud sandbox's start when it has none yet (a message queued offline).
    sendMessage: submitMessage,
    removeQueuedFollowUp,
    enqueueFollowUp,
  });
  const sendConversationAction = (text: string, answers?: string) => {
    if (preview || initialThreadHydrationStatus !== 'ready') return;
    const followUp = { text, files: [], ...(answers === undefined ? {} : { answers }) };
    if (busy) {
      const followUpId = crypto.randomUUID();
      // An answer to the agent's question goes through its claim, never through the queue.
      if (answers === undefined) enqueueFollowUp(chatId, followUp, followUpId);
      void steerFollowUp(followUp, followUpId)
        .then((accepted) => {
          if (accepted) removeQueuedFollowUp(chatId, followUpId);
        })
        .catch(() => undefined);
    } else {
      void submitMessage(followUp);
    }
  };
  useEffect(() => {
    if (selectedCloudRuntime !== null && connection.conversationId !== null)
      cloudDisconnectedRef.current = false;
  }, [connection.conversationId, selectedCloudRuntime]);
  const canSubmit =
    !conversationPlacementPending &&
    (ready ||
      (initialThreadHydrationStatus === 'ready' &&
        !preview &&
        !busy &&
        !cloudRepositoryBusy &&
        cloudAgentSelected &&
        cloudRuntime === null));
  usePendingMessageDrain({
    store: desktopStore,
    chatId,
    sendMessage,
    sendInBackground,
    ready,
  });
  const { editMessage, cancelMessageEdit } = createMessageEditControls({
    store: desktopStore,
    busy,
  });
  // The caret goes after the text being edited, without moving again as the user types.
  const focusComposerEnd = useEffectEvent(() => {
    const end = composerText.length;
    window.requestAnimationFrame(() => {
      composerRef.current?.focus();
      composerRef.current?.setSelectionRange(end, end);
    });
  });
  useEffect(() => {
    if (editingMessageId === null || editingMessageContainer === null) return;
    focusComposerEnd();
  }, [editingMessageContainer, editingMessageId]);
  useEffect(() => {
    if (preview) return;
    if (busy) {
      setLoadingThreadIds((current) => {
        if (current.has(chatId)) return current;
        return new Set(current).add(chatId);
      });
      return;
    }

    setLoadingThreadIds((current) => {
      if (!current.has(chatId)) return current;
      const next = new Set(current);
      next.delete(chatId);
      return next;
    });
  }, [busy, chatId, preview, setLoadingThreadIds]);
  const previewConversationTitle = useMemo(
    () => derivePreviewConversationTitle(messages, chatId),
    [messages, chatId],
  );
  const conversationTitle = preview ? previewConversationTitle : generatedConversationTitle;
  const { syncError } = useConversationPersistence({
    store: desktopStore,
    preview,
    hydrationReady: initialThreadHydrationStatus === 'ready',
    // Turns are persisted by the server in both profiles.
    serverPersisted: cloudAgentSelected || native === true,
    serverPersistenceSettled: !busy,
    chatId,
    title: conversationTitle,
    workspace,
    agent,
    collaboration: connection.collaboration,
    executionMode,
    modelId: connection.modelId ?? conversationModelId,
    messages,
    setMessages,
  });

  const { status: syncBootstrapStatus, retry: retryWorkspaceSync } = useWorkspaceSyncBootstrap({
    native,
    preview,
    reconcileRemovedProjects,
    setSyncedThreads,
    setSyncedWorkspaces,
    setSyncedAgentConfigs,
    setSyncedPullRequests,
    setWorkspaceSyncError,
  });

  useConversationTitleSync({
    preview,
    messages,
    agentName: connection.agentTitle,
    sessionTitle: [...messages].reverse().find((message) => message.metadata?.sessionTitle)
      ?.metadata?.sessionTitle,
    generatedTitle: generatedConversationTitle,
    setGeneratedTitle: setGeneratedConversationTitle,
  });
  const {
    activeThreadId: activeSidebarThreadId,
    openableThreads,
    projectKey: sidebarProjectKey,
    repositoryOptions,
    threads: sidebarThreads,
  } = useSidebarModel({
    store: desktopStore,
    preview,
    worktreeSourceRoot,
    workspace,
    messages,
    conversationTitle,
    cloudAgentSelected,
    cloudPullRequest,
    gitStatus,
    preferences: { removedProjects },
  });
  // A conversation's @ row says its assistant, from where they are filed.
  const directoryProjects = useWorkspaceDirectory((state) => state.projects);
  const directoryPlacements = useWorkspaceDirectory((state) => state.placements);
  const composerContextTargets = useMemo(
    (): readonly MentionTarget[] =>
      buildComposerContextTargets({
        panelMentionTargets,
        visibleAgentTerminals,
        sidebarThreads,
        currentConversationId: chatId,
        directory: {
          projects: directoryProjects,
          placements: {
            ...Object.fromEntries(
              sidebarThreads.flatMap((thread) =>
                thread.placement === undefined ? [] : [[thread.id, thread.placement]],
              ),
            ),
            ...directoryPlacements,
          },
        },
      }),
    [
      chatId,
      directoryPlacements,
      directoryProjects,
      panelMentionTargets,
      sidebarThreads,
      visibleAgentTerminals,
    ],
  );
  const unfilteredModelConfigOptions: readonly AcpSelectConfigOption[] = preview
    ? previewConfigOptions(previewModel, previewEffort)
    : connection.configOptions.filter(
        (option): option is AcpSelectConfigOption => option.type === 'select',
      );
  const modelConfigOptions = unfilteredModelConfigOptions;
  const {
    commandQuery,
    skillQuery,
    mentionQuery,
    mentionTargets,
    insertArtifact,
    insertMention,
    insertSkill,
    annotateSelection,
    askSelectionInSideChat,
  } = useConversationController({
    store: desktopStore,
    composerRef,
    composerText,
    composerContextTargets,
  });

  useConversationReferenceResolver({
    resolverRef: referenceResolverRef,
    chatId,
    conversationTitle,
    messages,
    conversationCache,
    sidebarThreads,
    visibleAgentTerminals,
    panelMentionTargets,
  });

  const {
    resetConversation,
    openEmptyConversation,
    selectThread,
    startThreadPointerDrag,
    activateSplitThread,
    activateSidebarThread,
    navigateConversation,
    openThreadInSplit,
  } = createConversationNavigation({
    store: desktopStore,
    preview,
    cloudRuntimeRef,
    suppressThreadClickRef,
    workspaceContainerRef,
    selectionRequestRef,
    getSidebarThreads: () => openableThreads,
    replaceMessages: (id, restored) => conversations.of(id).setMessages(restored),
    onSelectionSettled: (restored) => {
      if (
        !restored &&
        restoreInitialThread &&
        activeChatIdRef.current === initialChatIdRef.current
      ) {
        setInitialThreadHydrationStatus((status) => (status === 'loading' ? 'waiting' : status));
      }
    },
  });

  // In a split, the pane the user is in is the conversation in use.
  useSplitPaneFocus({
    store: desktopStore,
    containerRef: workspaceContainerRef,
    activate: (threadId) => void activateSplitThread(threadId),
  });

  const conversationOpener = useConversationOpenerValue(desktopStore, openableThreads, {
    selectThread,
    // A project's new conversation is a start screen: its composer takes the keys.
    openEmpty: (id) => {
      focusComposerAfterSwitchRef.current = true;
      openEmptyConversation(id);
    },
  });

  useLeaveUnlinkedCloud(desktopStore, resetConversation);
  const syncedThreadIds = useMemo(
    () => new Set(syncedThreads.map((thread) => thread.id)),
    [syncedThreads],
  );
  useInitialThreadHydration({
    preview,
    restoreInitialThread,
    chatId,
    syncBootstrapStatus,
    hydrationStatus: initialThreadHydrationStatus,
    setHydrationStatus: setInitialThreadHydrationStatus,
    // The addressed conversation may be one the sidebar does not list (an assistant's, a group).
    threads: openableThreads,
    syncedThreadIds,
    selectThread,
    initialChatIdRef,
    selectionRequestRef,
    hydrationRequestRef: initialHydrationRequestRef,
    hydrationAttemptsRef: initialHydrationAttemptsRef,
    hydrationRetryTimerRef: initialHydrationRetryTimerRef,
    setWorkspaceSyncError,
  });

  const {
    attachWorkspaceToConversation,
    chooseWorkspaceForHandoff,
    cloneSourceControlRepository,
    cloneWorkspaceForConversation,
    linkRepositoryLocally,
    selectWorkspace,
    startPersonalConversation,
    startProjectConversation,
  } = createRepositoryActions({
    store: desktopStore,
    conversations,
    preview,
    selectedCloudRuntimeActive: selectedCloudRuntime !== null,
    resetConversation,
    restoreProject,
    sidebarProjectKey,
  });

  const { resolveCloudHandoffRepository, loadCloudHandoffOptions } = createCloudHandoffOptions({
    store: desktopStore,
    cloudHandoffRepository,
    cloudHandoffBranches,
    setCloudHandoffRepository,
    setCloudHandoffBranches,
    setCloudHandoffRemoteNotice,
  });

  const connectionState = desktopConnectionState({
    preview,
    cloudAgentSelected,
    cloudRuntimeActive: cloudRuntime !== null,
    hasPendingCloudMessage: pendingCloudMessage !== null,
    connectionState: connection.status.state,
  });
  const cloudConversationStartupPhase = resolveCloudStartupPhase({
    preview,
    cloudAgentSelected,
    cloudRepositoryBusy,
    hasPendingCloudMessage: pendingCloudMessage !== null,
    cloudRuntimeActive: cloudRuntime !== null,
    sessionActive: connection.conversationId !== null,
    cloudProvisioningPhase,
    agentStartupPhase: connection.startupPhase,
  });
  const terminalPanelView = connection.pluginWorkbench.panels.find(
    (panel) => panel.session?.surface === 'terminal',
  )?.view;
  const cloudWorkspacePanelActive =
    sidePanelOpen &&
    (sidePanelView === 'review' ||
      sidePanelView === terminalPanelView ||
      sidePanelView === 'files');

  const { prepareManagedWorktree, handoffWorktree } = createWorktreeLifecycle({
    store: desktopStore,
    conversations,
    preview,
    worktreeSettings,
  });

  const {
    completeAgentHandoff,
    requestContinueInCloud,
    handOffToCloud,
    provisionCloudWorkspace,
    continueLocally,
    selectAgent,
  } = createAgentHandoff({
    store: desktopStore,
    conversations,
    executionMode,
    chatId,
    messageRevision,
    workspace,
    gitStatus,
    agents,
    agentId,
    localHandoffAgentId,
    localHandoffWorking,
    messages,
    agent,
    conversationTitle,
    modelId: connection.modelId ?? conversationModelId ?? '',
    cloudAgentSelected,
    cloudRuntime,
    busy,
    cloudHandoffWorking,
    cloudHandoffSource,
    cloudHandoffRepository,
    cloudHandoffAgentId,
    setCloudHandoffSource,
    setCloudHandoffAgentId,
    setCloudHandoffWorking,
    setCloudHandoffError,
    chatStoreRef,
    cloudRuntimeRef,
    cloudDisconnectedRef,
    pendingExecutionAfterLocalRef,
    prepareManagedWorktree,
    resolveCloudHandoffRepository,
    chooseWorkspaceForHandoff,
  });

  provisionCloudWorkspaceRef.current = provisionCloudWorkspace;
  const { changePlanFirst, changeModelConfig } = createConversationTurnControls({
    preview,
    busy,
    connection,
    modelConfigOptions,
    conversation: conversations.of(chatId),
    setPreviewModel,
    setPreviewEffort,
    // Chosen before the agent answered: the shell shows it only while its conversation is shown.
    setPreferredModelId: (value) =>
      conversations.of(chatId).whileShown((state) => state.setRestoredModelId(value)),
  });

  const branches = createBranchControls({
    store: desktopStore,
    conversations,
    preview,
    busy,
  });

  const { changeExecutionMode, continueOn, currentRuntimeMode, requestContinueLocally } =
    createExecutionModeControls({
      store: desktopStore,
      conversations,
      preview,
      getMessagesLength: () => messages.length,
      pendingExecutionAfterLocalRef,
      cloudHandoffRepository,
      cloudHandoffSource,
      setCloudHandoffSource,
      loadCloudHandoffOptions,
      requestContinueInCloud,
      handoffRemoteProvider,
      handoffWorktree,
      handoffExternalWorktree: async ({ workspace: nextWorkspace, branch: nextBranch }) => {
        if (agent === null) {
          desktopStore.getState().setBranchError('No local agent is available.');
          return false;
        }
        completeAgentHandoff({
          target: agent,
          nextWorkspace,
          direction: 'worktree-to-local',
          repository: sourceControlRepositoryFullName(nextWorkspace),
          branch: nextBranch,
        });
        return true;
      },
      prepareManagedWorktree,
      chooseWorkspaceForHandoff,
    });

  // Crowdex Cloud keeps no copy of the project just picked: its ways on, not a dead end.
  const missingProject = cloudProjectMissingCard({
    store: desktopStore,
    conversations,
    currentRuntimeMode,
    changeExecutionMode,
    startProjectConversation,
    chooseWorkspaceForHandoff,
    path: cloudProjectMissing,
    repositories: repositoryOptions,
    local: native === true,
    onChooseRepository: () => setAddRepositoryOpen(true),
  });

  const { runWorkspaceAction } = createWorkspaceActions({
    store: desktopStore,
    terminalView: terminalPanelView,
  });
  const failures = useConversationFailures({
    store: desktopStore,
    preview,
    agent,
    cloudAgentSelected,
    messageCount: messages.length,
    error,
    connectionStatus: connection.status,
    runWorkspaceAction,
    selectWorkspace,
    attach: attachWorkspaceToConversation,
    clone: cloneWorkspaceForConversation,
    handOffToCloud,
  });

  const composerSubmit = createComposerSubmit({
    store: desktopStore,
    preview,
    busy,
    canSubmit,
    conversationId: chatId,
    editingMessageId,
    oppositeFollowUpModeRef,
    messages,
    setMessages,
    submitMessage,
    enqueueFollowUp,
    removeQueuedFollowUp,
    steerFollowUp,
    prepareManagedWorktree,
    pickerOpen: commandQuery !== null || mentionQuery !== null || skillQuery !== null,
    cancelMessageEdit,
    personaRunsHere: connection.personaRunsHere,
    askPersona: connection.askPersona,
  });

  const shellNavigation = useShellNavigation({
    store: desktopStore,
    preview,
    previewConversation,
    phone,
    chatId,
    activeThreadId: activeSidebarThreadId,
    threads: openableThreads,
    busy,
    cloudAgentSelected,
    activateSidebarThread,
    openConversation: conversationOpener.openConversation,
    resetConversation,
    openThreadInSplit,
    startThreadPointerDrag,
    navigateConversation,
    stopAgentResponse,
    continueLocally,
    toggleSidebarHidden,
    addRepository: () => setAddRepositoryOpen(true),
  });
  // Review takes the width, and so does any panel in a narrow window (`useSidebarRail`); an app
  // never folds the sidebar, docked or maximized by the user.
  const narrowWindow = useMediaQuery(NARROW_DESKTOP_QUERY);
  const panelNeedsWidth = useStore(
    desktopStore,
    (state) =>
      state.sidePanelOpen && (narrowWindow || activePanelView(state.panelTabs) === 'review'),
  );
  const { mode: sidebarMode, expand: expandSidebar } = useSidebarRail({
    chosen: chosenSidebarMode,
    panelNeedsWidth,
    setChosen: setSidebarMode,
  });
  // Settings covers the whole window: the shell under it is out of the keyboard's reach.
  const windowCovered = useLibraryPage().coversWindow;
  // The window's traffic lights stay clear of the content while the sidebar is hidden (its
  // show button beside them) or a rail.
  const macTitleBarInset = useStore(desktopStore, (state) => state.macTitleBarInset);
  const leadingInset =
    sidebarMode === 'hidden'
      ? `${(macTitleBarInset > 0 ? macTitleBarInset : 12) + 40}px`
      : sidebarMode === 'rail' && macTitleBarInset > 0
        ? `${Math.max(0, macTitleBarInset - SIDEBAR_RAIL_WIDTH) + 16}px`
        : '0px';
  const account = {
    name: appSettings.displayName,
    email,
    profile: native === true ? 'This computer' : 'Crowdex Cloud',
  };

  if (native === null)
    return (
      <div className="flex h-dvh items-center justify-center text-muted-foreground">Loading…</div>
    );
  const conversationHeader: ConversationHeaderProps = {
    executionMode: currentRuntimeMode(),
    preview,
    conversationTitle,
    usage: latestUsage(messages),
    actions: {
      onRepositorySetup: () => void environment.openEnvironmentSettings(),
      // A cloud conversation's ⋮ brings it to this computer, after its confirmation (D6, D50).
      ...(cloudAgentSelected ? { onContinueLocally: () => requestContinueLocally() } : {}),
    },
  };

  // The conversation's summary, beside its transcript or in a popover from the title bar.
  const renderSummary = (visible: boolean) => (
    <ConversationSummary
      cloudHandoffWorking={cloudHandoffWorking}
      conversationRefs={conversationRefs}
      conversationSources={conversationSources}
      onContinueLocally={requestContinueLocally}
      onContinueOn={(destination) => void continueOn(destination)}
      onGenerateGitText={connection.generateGitText}
      onRunAction={runWorkspaceAction}
      phone={phone}
      preview={preview}
      runtimeMode={currentRuntimeMode()}
      runtimeSwitchDisabled={busy}
      visible={visible}
    />
  );

  // The open conversation and its side panel: laid out by the desktop shell or the phone app.
  const primaryConversation = (
    <ConversationActionsProvider
      busy={busy}
      messages={messages}
      onSend={sendConversationAction}
      ready={ready}
      respondToInteraction={respondToInteraction}
      setMessages={setMessages}
    >
      <ConversationPane
        onGroupMessagesChange={setMessages}
        chatId={chatId}
        composer={{
          agent,
          agentReady,
          availableSkills: preview ? PREVIEW_SKILLS : connection.availableSkills,
          busy,
          canSubmit,
          changeModelConfig,
          changePlanFirst,
          cloudAgentSelected,
          cloudConversationStartupPhase,
          cloudHandoffWorking,
          cloudWorkspacePanelActive,
          commandQuery,
          composerRef,
          collaboration: connection.collaboration,
          planFirst: connection.planFirst,
          insertArtifact,
          insertMention,
          insertSkill,
          mentionQuery,
          mentionTargets,
          messages,
          modelConfigOptions,
          onToolApproval: answerToolApproval,
          onClearFollowUps: () => clearQueuedFollowUps(chatId),
          onDeleteFollowUp: (id) => removeQueuedFollowUp(chatId, id),
          onKeyDown: composerSubmit.handleKeyDown,
          onReorderFollowUp: (draggedId, targetId) =>
            reorderQueuedFollowUp(chatId, draggedId, targetId),
          onSteerFollowUp: sendQueuedFollowUp,
          onStop: stopAgentResponse,
          onSubmit: composerSubmit.handleSubmit,
          needsYou: { threads: openableThreads, preview, onOpen: activateSidebarThread },
          preview,
          previewConversation,
          selectAgent: async (nextId) => {
            if (nextId !== agentId && !(await selectAgent(nextId)))
              throw new Error('Could not switch the ACP client.');
          },
          skillQuery,
          status,
          workspaceControls: (
            <ComposerMenuRow
              busy={busy}
              cloudHandoffBranches={cloudHandoffBranches}
              cloudHandoffRepository={cloudHandoffRepository}
              cloudHandoffSource={cloudHandoffSource}
              cloudHandoffWorking={cloudHandoffWorking}
              messageCount={messages.length}
              onAddRepository={() => setAddRepositoryOpen(true)}
              phone={phone}
              threads={openableThreads}
              onChangeExecutionMode={(mode) => void changeExecutionMode(mode)}
              onCloudHandoffError={setCloudHandoffError}
              onCloudHandoffSourceChange={setCloudHandoffSource}
              onLinkRepository={(repository, how) => {
                const { origin } = repository;
                if (origin?.forge == null || origin.logicalWorkspaceId === null) return;
                void linkRepositoryLocally({
                  logicalWorkspaceId: origin.logicalWorkspaceId,
                  name: repository.name,
                  forge: origin.forge,
                  how,
                });
              }}
              onLoadCloudHandoffOptions={loadCloudHandoffOptions}
              onOpenEnvironmentSettings={() => void environment.openEnvironmentSettings()}
              onStartPersonalConversation={() => void startPersonalConversation()}
              onStartProjectConversation={startProjectConversation}
              onRefreshRemoteBranches={() => void branches.refreshRemoteBranches()}
              onSwitchBranch={branches.switchBranch}
              repositoryOptions={repositoryOptions}
              runtimeMode={currentRuntimeMode()}
              selectedCloudRuntime={selectedCloudRuntime}
            />
          ),
          conversationId: chatId,
          onOpenEnvironment: () => void environment.openEnvironmentSettings(),
          composerText,
          setComposerText,
          editingMessageId,
          editingMessageContainer,
        }}
        errorBanners={{
          ...failures,
          cloudHandoffError,
          cloudHandoffRemoteNotice,
          syncError,
          onRetryTurn: sendConversationAction,
          started: displayedMessages.length > 0,
          onRetrySync: preview ? undefined : retryWorkspaceSync,
          // No cloud sandbox for the first message (H6): Runs in › This computer, where it can.
          onRunOnThisComputer:
            native === true ? () => void changeExecutionMode('local') : undefined,
          cloudProjectMissing: missingProject,
        }}
        header={phone ? null : { ...conversationHeader, connectionState }}
        split={workspaceSplit}
        transcript={{
          agent,
          displayedMessages,
          messages,
          onAnnotateSelection: annotateSelection,
          onAskSelectionInSideChat: askSelectionInSideChat,
          onEditMessage: !preview && !busy ? editMessage : undefined,
          onOpenSubagent: openSubagent,
          onRefreshAgents: () => void refreshAgents(),
          // A preview reviews the boards' diff.
          onLoadMessageDiff: preview
            ? async () => PREVIEW_REVIEW_DIFF
            : messageChanges.loadMessageDiff,
          onUndoMessageChanges: !preview && !busy ? messageChanges.undoMessageChanges : undefined,
          status,
        }}
      />
    </ConversationActionsProvider>
  );
  const sidePanel = (
    <ConversationSidePanel
      presentation={phone ? 'screen' : 'docked'}
      agent={agent}
      pluginPermissions={pluginPermissions}
      cloudAgentSelected={cloudAgentSelected}
      cloudConversationStartupPhase={cloudConversationStartupPhase}
      cloudProvisionError={cloudProvisionError}
      cloudPullRequest={cloudPullRequest}
      cloudRuntime={selectedCloudRuntime}
      generateGitText={connection.generateGitText}
      pluginConversation={{
        messages,
        busy,
        sendMessage: (text) => {
          if (!preview && ready) void submitMessage({ text, files: [] });
        },
      }}
      discoveringAgents={discoveringAgents}
      displayedPlan={displayedPlan}
      onAnnotateSelection={annotateSelection}
      onAskSelectionInSideChat={askSelectionInSideChat}
      onKillAgentTerminal={async (id, outputFile) => {
        if (outputFile === undefined) await killNativeAgentTerminal(id);
        else await killNativeAgentBackgroundTask(outputFile);
      }}
      onAgentTerminalStopped={markAgentTerminalStopped}
      resolveReference={(kind, id) => referenceResolverRef.current(kind, id)}
      onSubmitReview={(review) => void sendMessage({ text: review })}
      preview={preview}
      selectedSubagent={selectedSubagent}
      visibleAgentTerminals={visibleAgentTerminals}
    />
  );

  const dialogs = (
    <ConversationDialogs
      environment={environment}
      onCloneRepository={cloneSourceControlRepository}
      onContinueLocally={() => void continueLocally()}
      onCreateBranch={() => void branches.createBranch()}
      onFocusComposer={() => composerRef.current?.focus()}
      onOpenLocalWorkspace={selectWorkspace}
      onOpenWorktreeCheckout={branches.openWorktreeCheckout}
      onPlanRewind={(editedIndex) => messageChanges.planRewind(editedIndex)}
      onResendEdited={(restore) => messageChanges.resendEdited(restore)}
      onSwitchBranch={branches.performBranchSwitch}
      onCommitAndSwitchBranch={branches.commitAndSwitchBranch}
      pendingExecutionAfterLocalRef={pendingExecutionAfterLocalRef}
      preview={preview}
      worktreeSettings={worktreeSettings}
    />
  );

  return (
    <ConversationProviders
      opener={conversationOpener}
      store={desktopStore}
      workbench={connection.pluginWorkbench}
    >
      <ShellServices
        chatId={chatId}
        preview={preview}
        renderSummary={renderSummary}
        workspace={workspace}
      >
        <ShellNavigationProvider value={shellNavigation}>
          {phone ? (
            <MobileShell
              email={email}
              conversation={primaryConversation}
              conversationHeader={conversationHeader}
              conversationTitle={conversationTitle}
              dialogs={dialogs}
              sidePanel={sidePanel}
              started={messages.length > 0}
            />
          ) : (
            <div
              className="desktop-shell relative flex h-dvh overflow-hidden text-foreground"
              data-compact={appSettings.compactMode || undefined}
              data-sidebar-mode={sidebarMode}
              data-window-frame
              inert={windowCovered}
              data-theme={resolveDesktopTheme(appSettings.theme)}
            >
              <ShellSearchDialog />
              {sidebarMode === 'open' && (
                <DesktopSidebar account={account} onCollapse={() => setSidebarMode('rail')} />
              )}
              {sidebarMode === 'rail' && (
                <DesktopNavRail account={account} onExpand={expandSidebar} />
              )}
              <div
                className="desktop-frame relative flex min-h-0 min-w-0 flex-1 overflow-hidden bg-background"
                style={{ '--shell-leading-inset': leadingInset } as CSSProperties}
              >
                {sidebarMode === 'hidden' && (
                  <HiddenSidebar
                    account={account}
                    macTitleBarInset={macTitleBarInset}
                    onCollapse={() => setSidebarMode('rail')}
                    onShow={expandSidebar}
                    revealed={sidebarRevealed}
                    reveal={revealSidebar}
                    scheduleClose={scheduleSidebarClose}
                  />
                )}

                <WorkspaceDockLayout>
                  <main
                    className="desktop-workspace-container relative flex min-h-0 min-w-0 flex-1 flex-col"
                    data-split={workspaceSplit || undefined}
                    ref={workspaceContainerRef}
                  >
                    <ConversationDockOverlays />
                    <DesktopUpdateBanner />
                    <div className="relative flex min-h-0 min-w-0 flex-1">
                      <ConversationPanelRoot node={conversationLayout}>
                        <ConversationPaneSlot paneId={chatId}>
                          {primaryConversation}
                        </ConversationPaneSlot>
                        <SplitConversationPanes
                          agents={agents}
                          onActivate={(threadId) => void activateSplitThread(threadId)}
                          preview={preview}
                          resolveReference={(kind, id) => referenceResolverRef.current(kind, id)}
                        />
                      </ConversationPanelRoot>
                    </div>
                  </main>
                  {sidePanel}
                </WorkspaceDockLayout>
                {allAppsOpen && <AllAppsPage />}
              </div>
              {dialogs}
            </div>
          )}
        </ShellNavigationProvider>
      </ShellServices>
    </ConversationProviders>
  );
}
